/**
 * The trainer's brand colour: the six shipped presets, what a choice PAINTS in `/konto`'s phone
 * preview, and how it travels.
 *
 * The preset table itself is `shared/design-system/theme/brandPresets.ts` since 27 September 2026,
 * when the public form's Studio template became its second painter, and the reading of a choice
 * (`brandHues`) is `shared/design-system/theme/brandHues.ts` for the same reason; both are
 * re-exported here so this entity's surface did not move.
 *
 * ── The colour paints the trainer's OWN app too ──────────────────────────────────────────────
 * The signed-in web app wears the trainer's colour as the mobile app wears it on the phone (owner,
 * 23 September 2026). `brandHues` is the one reading of a choice both surfaces start from; the
 * app's derivation is `brandTokens.ts` beside this file.
 *
 * ── Three rules the wire imposes ─────────────────────────────────────────────────────────────
 * · A `PUT` body names EXACTLY ONE of `preset` or `customHex`, and a hex travels uppercase.
 * · Picking the app's own Flame is a RESET — `DELETE /me/brand-color` — never a `PUT` writing the
 *   default as though it were a choice. An absent row already answers `{ preset: 'Flame' }`.
 * · The contract types `preset` as a plain string, so codegen cannot narrow it to six names.
 *   `brandPresetByWire` is its only validator, and an unrecognised value falls back to the default
 *   rather than throwing: a colour is decoration and must never take a screen down.
 *
 * Nothing is refused on contrast: any `#RRGGBB` saves, and `shared/lib/contrast` moves the FILL
 * under a fixed white ink until the label reads.
 */

import type {
  TrainWithMeModulesClientsDtosRequestsSetBrandColorRequest as SetBrandColorRequest,
  TrainWithMeModulesClientsDtosResponsesBrandColorResponse as BrandColorResponse,
} from '~/shared/api/generated/model';

import {
  BRAND_PRESETS,
  brandGradientCss,
  brandPresetById,
  brandPresetByWire,
  type BrandPreset,
  type BrandPresetId,
} from '~/shared/design-system/theme/brandPresets';
import {
  BRAND_DARK_INK,
  BRAND_LIGHT_INK,
  brandHues,
  type BrandChoice,
} from '~/shared/design-system/theme/brandHues';
import { AA_TEXT_RATIO, contrastRatio, readableInk } from '~/shared/lib/contrast';

export type { BrandPreset, BrandPresetId, BrandPresetWire } from '~/shared/design-system/theme/brandPresets';
export { brandHues, type BrandHues } from '~/shared/design-system/theme/brandHues';
export {
  BRAND_PRESETS,
  brandGradientCss,
  brandPresetById,
  brandPresetByWire,
} from '~/shared/design-system/theme/brandPresets';

/** The app's own colour. Choosing it is the same as choosing nothing — see the header. */
export const DEFAULT_BRAND_PRESET_ID: BrandPresetId = 'flame';

export type BrandColor = BrandChoice;

export const DEFAULT_BRAND_COLOR: BrandColor = { kind: 'preset', preset: DEFAULT_BRAND_PRESET_ID };

/** The backend's `customHex` contract. */
const BRAND_HEX_PATTERN = /^#[0-9A-Fa-f]{6}$/;

export function isBrandHex(value: string): boolean {
  return BRAND_HEX_PATTERN.test(value);
}

/**
 * What the HEX field shows while it is being typed. No `#` — the field draws its own prefix — and a
 * half-typed value is not refused: `isBrandHex` decides whether there is a colour yet, so the
 * preview never flashes one nobody picked.
 */
export function normaliseHexInput(typed: string): string {
  return typed.replace(/[^0-9a-fA-F]/g, '').slice(0, 6).toUpperCase();
}

/** The preset a choice is, or the first one — the table's own fallback for an id off the wire. */
function presetOf(brand: BrandColor): BrandPreset {
  const fallback = BRAND_PRESETS[0];
  if (fallback === undefined) throw new Error('the brand preset table is empty');
  return brand.kind === 'preset' ? (brandPresetById(brand.preset) ?? fallback) : fallback;
}

/** True when this choice is the shipped ember — i.e. when saving it is a DELETE. */
export function isDefaultBrandColor(brand: BrandColor): boolean {
  return brand.kind === 'preset' && brand.preset === DEFAULT_BRAND_PRESET_ID;
}

/** Two choices are the same choice — what tells a dirty draft from a saved one. */
export function sameBrandColor(a: BrandColor, b: BrandColor): boolean {
  if (a.kind === 'preset' && b.kind === 'preset') return a.preset === b.preset;
  if (a.kind === 'custom' && b.kind === 'custom') return a.hex.toLowerCase() === b.hex.toLowerCase();
  return false;
}

/* ── The wire ─────────────────────────────────────────────────────────────────────────────── */

/** Wire → choice. A hex wins when both arrive; anything unrecognised reads as the default. */
export function toBrandColor(response: BrandColorResponse | null | undefined): BrandColor {
  if (response == null) return DEFAULT_BRAND_COLOR;
  const hex = typeof response.customHex === 'string' ? response.customHex.trim() : '';
  if (isBrandHex(hex)) return { kind: 'custom', hex: hex.toLowerCase() };
  const preset = typeof response.preset === 'string' ? brandPresetByWire(response.preset) : undefined;
  return preset === undefined ? DEFAULT_BRAND_COLOR : { kind: 'preset', preset: preset.id };
}

/** Choice → the cached wire shape, so an optimistic write and a server read agree. */
export function toBrandColorResponse(brand: BrandColor): BrandColorResponse {
  if (brand.kind === 'custom') return { preset: null, customHex: brand.hex.toUpperCase() };
  return { preset: presetOf(brand).wire, customHex: null };
}

/**
 * What saving a choice IS. A union rather than "a body, or null meaning delete": the reset is a
 * different verb, and a caller branching on `null` can forget which one `null` meant.
 */
export type BrandColorRequest =
  | { readonly kind: 'reset' }
  | { readonly kind: 'put'; readonly body: SetBrandColorRequest };

export function brandColorRequest(brand: BrandColor): BrandColorRequest {
  if (isDefaultBrandColor(brand)) return { kind: 'reset' };
  if (brand.kind === 'custom') return { kind: 'put', body: { customHex: brand.hex.toUpperCase() } };
  return { kind: 'put', body: { preset: presetOf(brand).wire } };
}

/* ── What a choice paints ─────────────────────────────────────────────────────────────────── */

/** The two surfaces the preview writes the colour on, from the mockup's `.screen` and `.c` fills. */
const PREVIEW_SURFACES = ['#f1efea', '#ffffff'] as const;

export interface BrandPaint {
  /** A filled accent surface: the preset's gradient, or one flat colour for a custom pick. */
  readonly fill: string;
  readonly ink: string;
  /** The one solid hue — a swatch, a dot, a ring. Never adjusted. */
  readonly solid: string;
  /** The same hue as WORDS on the preview's light surfaces, moved until it clears AA. */
  readonly markInk: string;
  /** `ink` against the hardest part of `fill` — what the AA chip reports. */
  readonly ratio: number;
}

/** Everything the preview and the tiles need from one choice, in one answer. */
export function brandPaint(brand: BrandColor): BrandPaint {
  const hues = brandHues(brand);
  const ink = hues.label === 'dark' ? BRAND_DARK_INK : BRAND_LIGHT_INK;
  return {
    fill: hues.custom ? hues.fill : brandGradientCss(hues.stops, hues.locations, '135deg'),
    ink,
    solid: hues.solid,
    markInk: readableInk(hues.solid, PREVIEW_SURFACES, AA_TEXT_RATIO, 'darken'),
    // The fill's own worst stop, not its middle: a ratio quoted off the easiest part of a fill is
    // a number about a pixel nobody reads.
    ratio: Math.min(...hues.stops.map((stop) => contrastRatio(ink, stop))),
  };
}
